/**
 * Regions Service
 * Handles all district and region-related database operations
 */

import { supabase } from '@/supabase.config';
import { District } from '@/models/district';
import { Region } from '@/models/region';
import { Database } from '@/types/supabase';
import { NotFoundError, DatabaseError } from '@/lib/errors';

// Type helpers for inserts and updates
type DistrictInsert = Database['public']['Tables']['districts']['Insert'];
type DistrictUpdate = Database['public']['Tables']['districts']['Update'];
type RegionInsert = Database['public']['Tables']['regions']['Insert'];
type RegionUpdate = Database['public']['Tables']['regions']['Update'];

// ============================================================================
// DISTRICTS
// ============================================================================

/**
 * Get all active districts with their regions
 * @throws {DatabaseError} If the database query fails
 */
export async function getAllDistricts(): Promise<District[]> {
  try {
    const { data, error } = await supabase
      .from('districts')
      .select(`
        *,
        regions:regions(*)
      `)
      .eq('is_active', true)
      .eq('regions.is_active', true)
      .order('name', { ascending: true });

    if (error) throw error;
    return data || [];
  } catch (error) {
    throw new DatabaseError('Failed to fetch districts', error);
  }
}

/**
 * Get a district by ID
 * @throws {NotFoundError} If district is not found
 * @throws {DatabaseError} If the database query fails
 */
export async function getDistrictById(id: string): Promise<District | null> {
  try {
    const { data, error } = await supabase
      .from('districts')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        throw new NotFoundError(`District with ID ${id} not found`);
      }
      throw error;
    }

    return data;
  } catch (error) {
    if (error instanceof NotFoundError) throw error;
    throw new DatabaseError(`Failed to fetch district with ID ${id}`, error);
  }
}

/**
 * Create a new district
 * @throws {DatabaseError} If the database operation fails
 */
export async function createDistrict(district: DistrictInsert): Promise<District> {
  try {
    const { data, error } = await supabase
      .from('districts')
      .insert(district as never)
      .select()
      .single();

    if (error) throw error;
    return data as District;
  } catch (error) {
    throw new DatabaseError('Failed to create district', error);
  }
}

/**
 * Update an existing district
 * @throws {NotFoundError} If district is not found
 * @throws {DatabaseError} If the database operation fails
 */
export async function updateDistrict(id: string, updates: DistrictUpdate): Promise<District> {
  try {
    const { data, error } = await supabase
      .from('districts')
      .update({ ...updates, updated_at: new Date().toISOString() } as never)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        throw new NotFoundError(`District with ID ${id} not found`);
      }
      throw error;
    }

    return data as District;
  } catch (error) {
    if (error instanceof NotFoundError) throw error;
    throw new DatabaseError(`Failed to update district with ID ${id}`, error);
  }
}

/**
 * Soft delete a district (sets is_active to false)
 * @throws {DatabaseError} If the database operation fails
 */
export async function deleteDistrict(id: string): Promise<void> {
  try {
    const { error } = await supabase
      .from('districts')
      .update({ is_active: false, updated_at: new Date().toISOString() } as never)
      .eq('id', id);

    if (error) throw error;
  } catch (error) {
    throw new DatabaseError(`Failed to delete district with ID ${id}`, error);
  }
}

// ============================================================================
// REGIONS
// ============================================================================

/**
 * Get all active regions with district information
 * @throws {DatabaseError} If the database query fails
 */
export async function getAllRegions(): Promise<Region[]> {
  try {
    const { data, error } = await supabase
      .from('regions')
      .select(`
        *,
        district:districts(id, name, code)
      `)
      .eq('is_active', true)
      .order('name', { ascending: true });

    if (error) throw error;
    return data || [];
  } catch (error) {
    throw new DatabaseError('Failed to fetch regions', error);
  }
}

/**
 * Get regions by district ID
 * @throws {DatabaseError} If the database query fails
 */
export async function getRegionsByDistrict(districtId: string): Promise<Region[]> {
  try {
    const { data, error } = await supabase
      .from('regions')
      .select('*')
      .eq('district_id', districtId)
      .eq('is_active', true)
      .order('name', { ascending: true });

    if (error) throw error;
    return data || [];
  } catch (error) {
    throw new DatabaseError(`Failed to fetch regions for district ${districtId}`, error);
  }
}

/**
 * Get a region by ID with district information
 * @throws {NotFoundError} If region is not found
 * @throws {DatabaseError} If the database query fails
 */
export async function getRegionById(id: string): Promise<Region | null> {
  try {
    const { data, error } = await supabase
      .from('regions')
      .select(`
        *,
        district:districts(id, name, code)
      `)
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        throw new NotFoundError(`Region with ID ${id} not found`);
      }
      throw error;
    }

    return data;
  } catch (error) {
    if (error instanceof NotFoundError) throw error;
    throw new DatabaseError(`Failed to fetch region with ID ${id}`, error);
  }
}

/**
 * Create a new region
 * @throws {DatabaseError} If the database operation fails
 */
export async function createRegion(region: RegionInsert): Promise<Region> {
  try {
    const { data, error } = await supabase
      .from('regions')
      .insert(region as never)
      .select()
      .single();

    if (error) throw error;
    return data as Region;
  } catch (error) {
    throw new DatabaseError('Failed to create region', error);
  }
}

/**
 * Update an existing region
 * @throws {NotFoundError} If region is not found
 * @throws {DatabaseError} If the database operation fails
 */
export async function updateRegion(id: string, updates: RegionUpdate): Promise<Region> {
  try {
    const { data, error } = await supabase
      .from('regions')
      .update({ ...updates, updated_at: new Date().toISOString() } as never)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        throw new NotFoundError(`Region with ID ${id} not found`);
      }
      throw error;
    }

    return data as Region;
  } catch (error) {
    if (error instanceof NotFoundError) throw error;
    throw new DatabaseError(`Failed to update region with ID ${id}`, error);
  }
}

/**
 * Soft delete a region (sets is_active to false)
 * @throws {DatabaseError} If the database operation fails
 */
export async function deleteRegion(id: string): Promise<void> {
  try {
    const { error } = await supabase
      .from('regions')
      .update({ is_active: false, updated_at: new Date().toISOString() } as never)
      .eq('id', id);

    if (error) throw error;
  } catch (error) {
    throw new DatabaseError(`Failed to delete region with ID ${id}`, error);
  }
}

// ============================================================================
// COMBINED QUERIES
// ============================================================================

/**
 * Get all districts with their associated regions
 * @throws {DatabaseError} If the database query fails
 */
export async function getAllDistrictsWithRegions(): Promise<District[]> {
  try {
    const districts = await getAllDistricts();
    const regions = await getAllRegions();

    return districts.map(district => ({
      ...district,
      regions: regions.filter(region => region.district_id === district.id),
    }));
  } catch (error) {
    throw new DatabaseError('Failed to fetch districts with regions', error);
  }
}
